import PropTypes from 'prop-types'
import { useState, useEffect, useRef } from 'react'
import Slider from '../../Inputs/Slider'
import {
  useMediaControlsContext,
  useMediaStateContext,
} from '../../MediaProvider/context'
import TimeDisplay from '../../TimeDisplay'
import { kuiperClassNames } from '../../utils/kuiperClassNames'

import './styles.scss'

const mutedClassname = 'muted'
const volumeClassRules = [
  { classname: mutedClassname, max: 0 },
  { classname: 'low', max: 0.33 },
  { classname: 'mid', max: 0.67 },
  { classname: 'high', max: 1 },
]

function convertVolumeSliderValue(changeVolume, volumeSliderValue) {
  if (changeVolume) {
    changeVolume(volumeSliderValue / 100)
  }
}

function getVolumeLevelClass(muted, volume) {
  const volumeClass = volumeClassRules.reduce((className, rule) => {
    if (!className && volume <= rule.max) return rule.classname
    return className
  }, '')

  return muted ? mutedClassname : volumeClass
}

function usePrevious(value) {
  const ref = useRef()

  useEffect(() => {
    ref.current = value
  })

  return ref.current
}

const MediaControls = ({
  getDuration = () => {},
  onPlayMedia = () => {},
  parentHovered = false,
}) => {
  const [isHovering, setHovering] = useState(false)
  const {
    changeVolume,
    toggleMuted,
    togglePlaying,
    setVideoPlayFromTime,
    setCurrentTime,
  } = useMediaControlsContext()
  const { duration, isPlaying, isMuted, volume, currentTime } =
    useMediaStateContext()
  const roundedDuration = Math.round(duration)
  const prevPlaying = usePrevious(isPlaying)
  const timeToDisplay =
    currentTime > roundedDuration ? roundedDuration : currentTime

  const onDragChange = (val) => {
    if (isPlaying) togglePlaying(false)
    setVideoPlayFromTime(val)
  }

  const onDragFinish = (val) => {
    if (!isPlaying) togglePlaying(true)
    setVideoPlayFromTime(val)
  }

  // reset video time and slider bar when the video completes
  useEffect(() => {
    if (prevPlaying && !isPlaying && currentTime >= roundedDuration) {
      setCurrentTime(0)
    }
    onPlayMedia(isPlaying)
  }, [
    isPlaying,
    prevPlaying,
    currentTime,
    roundedDuration,
    setCurrentTime,
    onPlayMedia,
  ])

  useEffect(() => {
    if (duration && getDuration) getDuration(duration)
  }, [duration, getDuration])

  return (
    <div
      className={kuiperClassNames('media-control-container', {
        'is-hovering': isHovering || parentHovered,
      })}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div className={kuiperClassNames('media-control')}>
        <button
          className={kuiperClassNames(isPlaying ? 'pause' : 'play')}
          type="submit"
          aria-label={isPlaying ? 'Pause' : 'Play'}
          onClick={togglePlaying}
        />

        <TimeDisplay seconds={timeToDisplay} className="current-time" />

        <div className={kuiperClassNames('media-controls-slider', 'seek')}>
          <Slider
            currentValue={currentTime}
            maxValue={roundedDuration}
            isActive={isPlaying}
            onChange={onDragChange}
            onChangeFinished={onDragFinish}
          />
        </div>

        <TimeDisplay seconds={roundedDuration} className="duration" />

        <button
          className={kuiperClassNames(
            'volume',
            getVolumeLevelClass(isMuted, volume)
          )}
          type="submit"
          aria-label="Mute"
          onClick={toggleMuted}
        />

        <div className={kuiperClassNames('media-controls-slider', 'volume')}>
          <Slider
            currentValue={isMuted ? 0 : volume * 100}
            maxValue={100}
            onChange={convertVolumeSliderValue.bind(null, changeVolume)}
          />
        </div>
      </div>
    </div>
  )
}

MediaControls.propTypes = {
  getDuration: PropTypes.func,
  parentHovered: PropTypes.bool,
  onPlayMedia: PropTypes.func,
}

export default MediaControls
